import React from 'react';
import { usePackagingOperationsContext } from '../context/PackagingOperationsContext';
import TemperaturesHumidity from './TemperaturesHumidity';
import CodeVerification from './CodeVerification';
import PAPA from './PAPA';
import PQI from './PQI';
import SealIntegrity from './SealIntegrity';
import CreamPercentage from './CreamPercentage';
import QualityWall from './QualityWall';

import { pkgOperationsService } from '../services/pkgOperationsService';

const ChecklistComponent: React.FC = () => {
    const { state, dispatch } = usePackagingOperationsContext();

    const handleSubmit = async () => {
        let deviations: any[] = [];

        if (state.sessionData.pkgopsType === 'Code Verification' && state.checklistData?.samples) {
            deviations = state.checklistData.samples
                .filter((s: any) => s.status === 'Not Okay')
                .map((s: any) => ({
                    defectDesc: s.defectCategory || "Unspecified Defect",
                    obsDetails: `Sample ${s.id + 1} - Defect Count: ${s.defectCount}`,
                    sampleId: s.id
                }));
        } else if (state.sessionData.pkgopsType === 'PAPA' && state.checklistData) {
            const { metadata, defects, activeDefects, stats } = state.checklistData;
            const tourId = state.sessionData.tourId || `QT-${Date.now()}`;
            const userName = state.sessionData.qaExecutive || null;

            // Save PAPA data to Dataverse (and offline queue)
            try {
                const listToSave = activeDefects || (defects || []).filter((d: any) => d.selected && d.count > 0);
                await pkgOperationsService.savePapaData({
                    tourId,
                    productName: metadata?.productName || state.sessionData.site || '',
                    sku: metadata?.sku || metadata?.productName || '',
                    sampleSize: metadata?.sampleSize || 100,
                    defects: listToSave,
                    totalDefectCount: stats?.totalDefects || 0,
                    totalDefectPct: stats?.totalDefectPct || 0,
                    categoryACount: stats?.catACount || 0,
                    categoryBCount: stats?.catBCount || 0,
                    categoryCCount: stats?.catCCount || 0,
                    categoryAPct: stats?.catAPct || 0,
                    categoryBPct: stats?.catBPct || 0,
                    categoryCPct: stats?.catCPct || 0,
                    userName
                });
            } catch (err) {
                console.warn('Error saving PAPA to Dataverse:', err);
            }

            // Collect deviations for each defect
            const defectList = (activeDefects && activeDefects.length > 0)
                ? activeDefects
                : (defects || []).filter((d: any) => d.selected && d.count > 0);

            deviations = defectList.map((d: any) => ({
                defectDesc: `${d.category === 'A' ? '[Critical - Cat A] ' : d.category === 'B' ? '[Major - Cat B] ' : '[Minor - Cat C] '}${d.name}${d.remarks ? ` - Note: ${d.remarks}` : ''}`,
                obsDetails: `Category ${d.category} (${d.type}) - Count: ${d.count} (${d.pct ? (typeof d.pct === 'number' ? d.pct.toFixed(2) + '%' : d.pct) : ''})`,
                sampleId: `PAPA - ${d.name}`
            }));
        } else if (state.sessionData.pkgopsType === 'Seal Integrity' && state.checklistData) {
            const { productName, sku, machines, metadata } = state.checklistData;

            // Save multi-machine records to Dataverse (and offline queue)
            try {
                if (machines && Array.isArray(machines)) {
                    await pkgOperationsService.saveSealIntegrityData({
                        tourId: state.sessionData.tourId || `QT-${Date.now()}`,
                        productName: productName || state.sessionData.site || '',
                        sku: sku || productName || '',
                        machines: machines,
                        userName: state.sessionData.qaExecutive || null
                    });
                }
            } catch (err) {
                console.warn('Error saving Seal Integrity to Dataverse:', err);
            }

            // Collect deviations for each machine that has leakages
            if (machines && Array.isArray(machines)) {
                machines.forEach((m: any) => {
                    if (m.totalLeaks > 0) {
                        deviations.push({
                            defectDesc: `Machine ${m.machineNo}: ${m.formattedLeakageType || 'Seal Leakage Detected'}`,
                            obsDetails: `M/C ${m.machineNo} - Leakage Count: ${m.totalLeaks} / ${m.sampleQty} (${m.passPctStr || (m.passPct + '%')} Pass)`,
                            sampleId: `Seal Integrity - ${m.machineNo}`
                        });
                    }
                });
            } else if (metadata && metadata.leakageCount > 0) {
                deviations = [{
                    defectDesc: metadata.leakageType || "Leakage Detected",
                    obsDetails: `Leakage Count: ${metadata.leakageCount}`,
                    sampleId: "Seal Integrity"
                }];
            }
        } else if (state.sessionData.pkgopsType === 'Quality Wall Records' && state.checklistData) {
            const qw = state.checklistData.qualityWall;
            const tourId = state.sessionData.tourId || `QT-${Date.now()}`;
            const userName = state.sessionData.qaExecutive || null;

            if (qw) {
                try {
                    await pkgOperationsService.saveQualityWallData({
                        tourId,
                        productName: qw.productName || state.sessionData.site || '',
                        sku: qw.sku || '',
                        lineNo: qw.lineNo || state.sessionData.lineNo || '',
                        batchNo: qw.batchNo || '',
                        wallType: qw.wallType || 'Routine Wall',
                        facilitator: qw.facilitator || userName || '',
                        members: qw.members || [],
                        overallScore: qw.overallScore || 0,
                        overallStatus: qw.overallStatus || 'Green Wall',
                        overallAction: qw.overallAction || 'Sustain',
                        remarks: qw.remarks || '',
                        userName
                    });
                } catch (err) {
                    console.warn('Error saving Quality Wall to Dataverse:', err);
                }

                if (qw.overallAction === 'Abort' || qw.overallStatus === 'Hot Wall') {
                    deviations = [{
                        defectDesc: `Quality Wall Critical Abort (Hot Wall - Score ${qw.overallScore})`,
                        obsDetails: `Immediate line halt required. Product: ${qw.productName}, Batch: ${qw.batchNo}. Facilitator: ${qw.facilitator}. Remarks: ${qw.remarks || 'Critical defect detected during Quality Wall'}`,
                        sampleId: "Quality Wall (Hot Wall)"
                    }];
                } else if (qw.overallAction === 'Action Taken' || qw.overallStatus === 'Yellow Wall') {
                    deviations = [{
                        defectDesc: `Quality Wall Action Taken (Yellow Wall - Score ${qw.overallScore})`,
                        obsDetails: `Process deviation observed. Corrective action taken on the line. Product: ${qw.productName}, Batch: ${qw.batchNo}. Remarks: ${qw.remarks || 'Process adjustments required'}`,
                        sampleId: "Quality Wall (Yellow Wall)"
                    }];
                }
            }
        } else if (state.sessionData.pkgopsType === 'Cream Percentage' && state.checklistData) {
            const { metadata, machines, weightData, averageCreamPercentage, status } = state.checklistData;
            const machineList = machines && machines.length > 0 ? machines : [{
                machineNo: metadata?.machineNo || 'M/C-01',
                sandwichWeights: weightData?.sandwichWeights || ['', '', '', ''],
                shellWeights: weightData?.shellWeights || ['', '', '', ''],
                averageCreamPercentage: averageCreamPercentage || '0.00',
                status: status || { text: 'Pass' }
            }];

            for (let i = 0; i < machineList.length; i++) {
                const m = machineList[i];
                try {
                    await pkgOperationsService.saveCreamPercentageData({
                        cycleNum: i + 1,
                        formData: {
                            productName: metadata?.productName || '',
                            machineNo: m.machineNo || `M/C-0${i + 1}`,
                            line: metadata?.line || state.sessionData.lineNo || '',
                            standardCreamPercentage: metadata?.standardCreamPercentage || '28.00',
                            executiveName: metadata?.executiveName || state.sessionData.qaExecutive || ''
                        },
                        weightData: {
                            sandwichWeights: m.sandwichWeights,
                            shellWeights: m.shellWeights
                        },
                        qualityTourId: state.sessionData.tourId || `QT-${Date.now()}`,
                        userName: state.sessionData.qaExecutive || null,
                        shiftValue: state.sessionData.shift || 'Shift 1'
                    });
                } catch (err) {
                    console.warn(`Error saving Cream Percentage for ${m.machineNo}:`, err);
                }

                if (m.status?.text === 'Fail') {
                    deviations.push({
                        defectDesc: `Cream Percentage Out of Bounds (${m.machineNo})`,
                        obsDetails: `Calculated Avg Cream %: ${m.averageCreamPercentage}%, Standard: ${metadata?.standardCreamPercentage || '28.00'}% (Min: ${metadata?.standardMin || '26.00'}% - Max: ${metadata?.standardMax || '30.00'}%)`,
                        sampleId: `Cream % - ${m.machineNo}`
                    });
                }
            }
        } else if (state.sessionData.pkgopsType === 'PQI' && state.checklistData) {
            const { productName, sku, pkd, batchCode, standardWeight, nwSamples, averageWeight, giveAway, isUnderweight, avgWeight, shortfall } = state.checklistData;
            const tourId = state.sessionData.tourId || `QT-${Date.now()}`;
            const userName = state.sessionData.qaExecutive || null;

            // Save Net Weight to Dataverse
            try {
                const validWeights = (nwSamples || []).map((w: any) => parseFloat(w)).filter((w: number) => !isNaN(w) && w > 0);
                if (validWeights.length > 0) {
                    await pkgOperationsService.savePqiNetWeightData({
                        tourId,
                        productName: productName || state.sessionData.site || '',
                        sku: sku || productName || '',
                        standardWeight: standardWeight || 150,
                        averageWeight: averageWeight || 0,
                        giveAway: giveAway || 0,
                        sampleWeights: validWeights,
                        userName
                    });
                }
            } catch (err) {
                console.warn('Error saving PQI Net Weight to Dataverse:', err);
            }

            // Save Evaluations (Product, Primary, Secondary, CBB)
            try {
                const evalTypes: Array<'Product' | 'Primary' | 'Secondary' | 'CBB'> = ['Product', 'Primary', 'Secondary', 'CBB'];
                for (const t of evalTypes) {
                    const sampleList = t === 'Product' ? state.checklistData.productSamples
                        : t === 'Primary' ? state.checklistData.primarySamples
                        : t === 'Secondary' ? state.checklistData.secondarySamples
                        : state.checklistData.cbbSamples;
                    if (sampleList && sampleList.length > 0) {
                        await pkgOperationsService.savePqiEvaluationData({
                            tourId,
                            evaluationType: t,
                            productName: productName || state.sessionData.site || '',
                            sku: sku || productName || '',
                            pkd,
                            batchCode,
                            samples: sampleList,
                            userName
                        });
                    }
                }
            } catch (err) {
                console.warn('Error saving PQI Evaluations to Dataverse:', err);
            }

            // Check if Net Weight is underweight
            if (isUnderweight) {
                deviations.push({
                    defectDesc: "PQI Net Weight Shortfall (Underweight)",
                    obsDetails: `Observed Average Weight (${avgWeight}) is below Standard (${standardWeight}g). Shortfall: ${shortfall}g`,
                    sampleId: "PQI Net Weight"
                });
            }

            // Collect pack sample deviations
            const mapEval = (samples: any[], prefix: string) => (samples || [])
                .filter(s => s.status === 'Not Okay')
                .map(s => ({
                    defectDesc: `${prefix}: ${s.category || 'Unspecified Defect'}`,
                    obsDetails: s.detail ? `Sample ${s.id + 1} - ${s.detail}` : `Sample ${s.id + 1}`,
                    sampleId: `${prefix} ${s.id + 1}`
                }));

            deviations = [
                ...deviations,
                ...mapEval(state.checklistData.productSamples || [], 'Product'),
                ...mapEval(state.checklistData.primarySamples || [], 'Primary'),
                ...mapEval(state.checklistData.secondarySamples || [], 'Secondary'),
                ...mapEval(state.checklistData.cbbSamples || [], 'CBB')
            ];
        }

        // Add additional checklist type deviation logic here as they are wired up...

        if (deviations.length > 0) {
            dispatch({ type: 'SET_DEVIATIONS', payload: deviations });
            dispatch({ type: 'SET_STATE', payload: 'PENDING_PRODUCTION' });
        } else {
            dispatch({ type: 'SET_STATE', payload: 'COMPLETED' });
        }
    };

    const renderSubChecklist = () => {
        switch (state.sessionData.pkgopsType) {
            case "Temperatures & Humidity":
                return <TemperaturesHumidity />;
            case "Code Verification":
                return <CodeVerification />;
            case "PAPA":
                return <PAPA />;
            case "PQI":
                return <PQI />;
            case "Seal Integrity":
                return <SealIntegrity />;
            case "Cream Percentage":
                return <CreamPercentage />;
            case "Quality Wall Records":
                return <QualityWall />;
            default:
                return <div className="p-4 text-red-500">Invalid Checklist Type</div>;
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 rounded-t-lg">
                <h2 className="text-lg font-semibold text-slate-800">Checklist Parameters Entry</h2>
            </div>

            {renderSubChecklist()}

            <div className="p-4 border-t border-gray-200 text-right bg-gray-50 rounded-b-lg">
                <button
                    onClick={handleSubmit}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    Submit Checklist
                </button>
            </div>
        </div>
    );
};

export default ChecklistComponent;
